import React, { useEffect, useState } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Button,
  TextInput,
  Textarea,
  Select,
  Toggle,
  Badge,
  Trash01,
  File01,
  Settings01,
  BookOpen01,
  LayoutAlt01,
  CurrencyDollar,
  CheckCircle,
  InfoCircle,
  Plus,
} from "@opendate-app/setlist";
import WizardShell, { ReviewRow, ReviewSection, WizardCard } from "../ClassesShared/WizardShell";
import PriceInput, { formatPriceInput } from "../ClassesShared/PriceInput";
import TintedAlert from "../ClassesShared/TintedAlert";
import { FORMAT_LABELS } from "../ClassesShared/ClassFormatBadge";
import DragHandle from "../ClassesShared/DragHandle";
import TemplateSectionsEditor from "../ClassesShared/TemplateSectionsEditor";
import ClassImageField from "../ClassesShared/ClassImageField";
import useImageUpload from "../ClassesShared/useImageUpload";
import toFormData from "../ClassesShared/toFormData";
import useWizardSteps, { newSessionKey, toggleChecked } from "../ClassesShared/useWizardSteps";
import useFocusAfterRemove from "../ClassesShared/useFocusAfterRemove";
import sortableAnnouncements, { SORTABLE_INSTRUCTIONS } from "../ClassesShared/sortableAnnouncements";
import {
  keptSections,
  sectionHasBody,
  sectionHeading,
  sectionsAttributes,
  sectionsError,
  sectionsFromTemplate,
  suggestedSections,
} from "../ClassesShared/templateSections";
import { formatPrice } from "../../shared/teamCurrencyFormatting";
import { capacityError, priceError } from "../ClassScheduleWizard/pricingValidation";
import { NAME_MAX_LENGTH } from "../ClassScheduleWizard/scheduleValidation";
import { parseDurationToMinutes, minutesToDurationText } from "./duration";
import "./classTemplateWizard.css";
import "../ClassesShared/classesShared.css";

const STEPS = [
  { key: "basics", label: "Basics", icon: File01, subtitle: "What this class is and who it's for." },
  { key: "defaults", label: "Format & Defaults", icon: Settings01, subtitle: "Cascade these to every instance you schedule. Override per instance later." },
  { key: "curriculum", label: "Curriculum", icon: BookOpen01, subtitle: "Name each session and optionally describe what it covers. These flow into every class scheduled from this template so you don't re-enter them each time." },
  { key: "content", label: "Page Content", icon: LayoutAlt01, subtitle: "Sections shown on every public class page from this template, below the description. Sections with no text stay hidden." },
  { key: "pricing", label: "Pricing & Policies", icon: CurrencyDollar, subtitle: "Defaults that apply to every new class scheduled from this template. Per-instance settings can override." },
  { key: "review", label: "Review", icon: CheckCircle, subtitle: "Double-check everything before saving. Click any section to jump back and edit." },
];

function SortableSessionRow({ session, index, nameError, onChange, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: session.key });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`class-template-wizard__session-row${isDragging || transform ? " class-template-wizard__session-row--moving" : ""}`}
      role="group"
      aria-label={`Session ${index + 1}`}
    >
      <WizardCard>
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div className="d-flex align-items-center classes-gap-8">
            <DragHandle label={`Reorder session ${index + 1}`} {...attributes} {...listeners} />
            <Badge color="brand" size="sm" dot>{`SESSION ${index + 1}`}</Badge>
          </div>
          <span className="classes-danger-icon">
            <Button variant="tertiary" size="sm" icon={Trash01} aria-label={`Remove session ${index + 1}`} data-template-session-remove onClick={() => onRemove(session.key, index)} />
          </span>
        </div>
        <TextInput
          data-field={`template-session-${session.key}`}
          label="Name"
          error={nameError}
          placeholder="e.g. Day 1 — Setup & First Cuts"
          value={session.name}
          onChange={(e) => onChange(session.key, "name", e.target.value)}
        />
        <div className="mt-3">
          <Textarea
            label="Description (optional)"
            rows={3}
            placeholder="What this session covers."
            value={session.description}
            onChange={(e) => onChange(session.key, "description", e.target.value)}
          />
        </div>
      </WizardCard>
    </div>
  );
}

SortableSessionRow.propTypes = {
  session: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  nameError: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

SortableSessionRow.defaultProps = { nameError: null };

function ClassTemplateWizardApp(props) {
  const {
    classTemplate,
    createPath,
    updatePath,
    indexPath,
    csrfToken,
    formatOptions,
    categoryOptions,
    waitlistWindowOptions,
    waitlistWindowLabels,
    teamWaitlistDefault,
    currencyCode,
  } = props;
  const isEditing = !!classTemplate;

  const focusAfterRemove = useFocusAfterRemove();

  const [form, setForm] = useState(() => ({
    name: classTemplate?.name || "",
    category: classTemplate?.category || "",
    internal_code: classTemplate?.internal_code || "",
    description: classTemplate?.description || "",
    format: classTemplate?.format || formatOptions[0] || "in_person",
    durationText: minutesToDurationText(classTemplate?.default_session_duration_minutes),
    default_capacity: classTemplate?.default_capacity ?? "",
    default_price: formatPriceInput(classTemplate?.default_price ?? ""),
    default_waitlist_enabled: classTemplate?.default_waitlist_enabled ?? teamWaitlistDefault,
    default_waitlist_claim_window_hours:
      classTemplate?.default_waitlist_claim_window_hours || waitlistWindowOptions[0],
  }));

  const [sessions, setSessions] = useState(() =>
    (classTemplate?.class_template_sessions || []).map((s) => ({
      key: `existing-${s.id}`,
      id: s.id,
      name: s.name || "",
      description: s.description || "",
    }))
  );

  const [sections, setSections] = useState(() =>
    (isEditing ? sectionsFromTemplate(classTemplate.class_template_sections) : suggestedSections())
  );

  const image = useImageUpload(classTemplate?.main_image_url);

  const setField = (key, val) => setForm((prev) => ({ ...prev, [key]: val }));

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const durationInMinutes = parseDurationToMinutes(form.durationText);
  const nameValid = form.name.trim().length > 0 && form.name.trim().length <= NAME_MAX_LENGTH;

  const sessionCountLabel = sessions.length > 0 ? String(sessions.length) : "";
  const [sessionCountInput, setSessionCountInput] = useState(sessionCountLabel);
  useEffect(() => {
    setSessionCountInput(sessionCountLabel);
  }, [sessionCountLabel]);

  const isBlankSession = (s) => !s.id && s.name.trim() === "" && (s.description || "").trim() === "";
  const keptSessionsList = sessions.filter((s) => !isBlankSession(s));

  const defaultsErrors = {};
  const sessionCountText = String(sessionCountInput).trim();
  if (sessionCountText !== "" && sessionCountText !== sessionCountLabel && (!/^\d+$/.test(sessionCountText) || Number(sessionCountText) < 1)) defaultsErrors.sessionCount = "Enter a whole number of 1 or more.";
  const capacityMessage = capacityError(form.default_capacity, { required: false });
  if (capacityMessage) defaultsErrors.capacity = capacityMessage;
  if (String(form.durationText || "").trim() !== "" && !durationInMinutes) defaultsErrors.duration = "Enter a length like \"90 minutes\" or \"3 hours\".";
  const defaultsInvalid = Object.keys(defaultsErrors).length > 0;
  const defaultPriceError = priceError(form.default_price, { required: false });

  const isStepComplete = (index) => {
    switch (index) {
      case 0:
        return nameValid;
      case 1:
        return !!form.format && !defaultsInvalid;
      case 2:
        return keptSessionsList.length > 0 && keptSessionsList.every((s) => s.name.trim() !== "");
      case 3:
        return !sectionsError(sections);
      case 4:
        return !defaultPriceError;
      default:
        return false;
    }
  };

  const wizard = useWizardSteps({
    steps: STEPS,
    isEditing,
    isStepComplete,
    resetOn: [form, sessions, sections, sessionCountInput, image.imageUrl],
  });
  const { step, saving, fieldErrors, goTo } = wizard;

  const growSessionsTo = (raw) => {
    setSessionCountInput(raw);
    if (!/^\d+$/.test(raw.trim())) return;
    const n = Number(raw.trim());
    if (n <= sessions.length) return;
    setSessions((prev) => [
      ...prev,
      ...Array.from({ length: n - prev.length }, () => ({ key: newSessionKey(), name: "", description: "" })),
    ]);
  };

  const addSession = () => setSessions((prev) => [...prev, { key: newSessionKey(), name: "", description: "" }]);
  const changeSession = (key, field, value) =>
    setSessions((prev) => prev.map((s) => (s.key === key ? { ...s, [field]: value } : s)));
  const removeSession = (key, index) => {
    focusAfterRemove("[data-template-session-remove]", index, "[data-template-session-add]");
    setSessions((prev) => prev.filter((s) => s.key !== key));
  };
  const sessionKeys = sessions.map((s) => s.key);
  const sessionLabel = (key) => {
    const index = sessionKeys.indexOf(key);
    const name = index >= 0 && sessions[index].name.trim();
    return name ? `session "${name}"` : `session ${index + 1}`;
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setSessions((prev) => {
      const oldIndex = prev.findIndex((s) => s.key === active.id);
      const newIndex = prev.findIndex((s) => s.key === over.id);
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const buildPayload = (keptSessions) => {
    const sessionsAttributes = keptSessions.map((s, idx) => {
      const attr = { name: s.name, description: s.description, position: idx + 1 };
      if (s.id) attr.id = s.id;
      return attr;
    });

    if (isEditing) {
      const keptIds = new Set(keptSessions.filter((s) => s.id).map((s) => s.id));
      (classTemplate.class_template_sessions || []).forEach((s) => {
        if (!keptIds.has(s.id)) sessionsAttributes.push({ id: s.id, _destroy: true });
      });
    }

    return {
      name: form.name.trim(),
      category: form.category,
      internal_code: form.internal_code,
      description: form.description,
      format: form.format,
      default_session_duration_minutes: durationInMinutes,
      default_capacity: form.default_capacity === "" ? null : form.default_capacity,
      default_price: form.default_price === "" ? null : form.default_price,
      default_waitlist_enabled: form.default_waitlist_enabled,
      default_waitlist_claim_window_hours: form.default_waitlist_claim_window_hours,
      class_template_sessions_attributes: sessionsAttributes,
      class_template_sections_attributes: sectionsAttributes(sections, classTemplate?.class_template_sections),
      ...image.payload,
    };
  };

  const problem = () => {
    if (!nameValid) {
      return { step: 0, field: "template-name", message: form.name.trim() === "" ? "Give the template a name." : `Template names can be up to ${NAME_MAX_LENGTH} characters.` };
    }
    if (defaultsInvalid) {
      const field = ["sessionCount", "duration", "capacity"].find((key) => defaultsErrors[key]);
      return { step: 1, field: `template-${field}`, message: "Fix the format and defaults before saving.", inline: true };
    }
    if (defaultPriceError) return { step: 4, field: "template-default-price", message: "Fix the default price before saving.", inline: true };
    const unnamedSession = keptSessionsList.find((s) => s.name.trim() === "");
    if (unnamedSession) return { step: 2, field: `template-session-${unnamedSession.key}`, message: "Every curriculum session needs a name." };
    const sectionMessage = sectionsError(sections);
    if (sectionMessage) {
      const unheaded = keptSections(sections).find((section) => sectionHeading(section) === "");
      return { step: 3, field: `section-${unheaded.key}`, message: sectionMessage };
    }
    return null;
  };

  const submit = async () => {
    if (saving) return;
    const found = problem();
    if (found) {
      wizard.showProblem(found);
      return;
    }

    wizard.startSaving();
    const headers = { "X-CSRF-Token": csrfToken };
    try {
      const body = toFormData({ class_template: buildPayload(keptSessionsList) });
      if (updatePath) {
        await axios.patch(updatePath, body, { headers });
      } else {
        await axios.post(createPath, body, { headers });
      }
      window.location.href = updatePath || indexPath;
    } catch (e) {
      wizard.failSave(e, "Could not save template.");
    }
  };

  const categoryChoices = form.category && !categoryOptions.includes(form.category)
    ? [form.category, ...categoryOptions]
    : categoryOptions;

  return (
    <WizardShell
      title={isEditing ? "Edit Template" : "New Template"}
      subtitle="Templates are the canonical definition of a course. They save time when you schedule the same class over and over."
      cancelHref={indexPath}
      saveLabel="Save Template"
      onSave={submit}
      errorTitle="Could not save template"
      setupTitle="Template setup"
      wizard={wizard}
      nextDisabled={(wizard.stepKey === "defaults" && defaultsInvalid) || (wizard.stepKey === "pricing" && !!defaultPriceError)}
      finish={<Button variant="primary" onClick={submit} aria-disabled={saving || undefined}>{saving ? "Saving…" : "Save Template"}</Button>}
    >
      {step === 0 && (
        <WizardCard>
          <TextInput
            data-field="template-name"
            label="Name"
            required
            error={fieldErrors["template-name"]}
            placeholder="e.g. Vinyl 101: Intro to DJing"
            maxLength={NAME_MAX_LENGTH}
            value={form.name}
            onChange={(e) => setField("name", e.target.value)}
          />
          <div className="classes-form-row mt-4">
            <Select
              label="Category"
              searchable
              clearable
              placeholder="Select or create a category…"
              formatCreateLabel={(input) => `Create "${input}"`}
              options={categoryChoices.map((c) => ({ value: c, label: c }))}
              value={form.category || null}
              onChange={(value) => setField("category", value || "")}
              onCreateOption={(value) => setField("category", value)}
            />
            <TextInput
              label="Internal code"
              hint="Optional — for your own catalog reference."
              placeholder="VINYL-101"
              value={form.internal_code}
              onChange={(e) => setField("internal_code", e.target.value)}
            />
          </div>
          <div className="mt-4">
            <Textarea
              label="Description"
              rows={4}
              placeholder="What registrants will get out of this class. This shows up on the public class page."
              value={form.description}
              onChange={(e) => setField("description", e.target.value)}
            />
          </div>
          <div className="mt-4">
            <ClassImageField
              label="Image"
              hint="New classes scheduled from this template start with this image."
              imageUrl={image.imageUrl}
              fileName={image.fileName}
              onImage={image.upload}
              onRemove={image.remove}
            />
          </div>
        </WizardCard>
      )}

      {step === 1 && (
        <WizardCard>
          <div className="classes-form-row">
            <Select
              label="Format"
              options={formatOptions.map((f) => ({ value: f, label: FORMAT_LABELS[f] || f }))}
              value={form.format}
              onChange={(v) => setField("format", v)}
            />
            <TextInput
              data-field="template-sessionCount"
              label="Default # of sessions"
              type="number"
              min="1"
              step="1"
              hint="A single-session class is just 1. Name each session in the next step."
              error={defaultsErrors.sessionCount}
              value={sessionCountInput}
              onChange={(e) => growSessionsTo(e.target.value)}
              onBlur={() => { if (!defaultsErrors.sessionCount) setSessionCountInput(sessionCountLabel); }}
            />
          </div>
          <div className="classes-form-row mt-4">
            <TextInput
              data-field="template-duration"
              label="Default session duration"
              placeholder="e.g. 3 hours"
              error={defaultsErrors.duration}
              value={form.durationText}
              onChange={(e) => setField("durationText", e.target.value)}
            />
            <TextInput
              data-field="template-capacity"
              label="Default capacity"
              type="number"
              min="1"
              step="1"
              placeholder="e.g. 20"
              error={defaultsErrors.capacity}
              value={form.default_capacity}
              onChange={(e) => setField("default_capacity", e.target.value)}
            />
          </div>
        </WizardCard>
      )}

      {step === 2 && (
        <>
          {sessions.length === 0 && (
            <WizardCard>
              <div className="classes-wizard__setting-hint">
                No sessions yet. Add one below, or set a default number of sessions on the previous step.
              </div>
            </WizardCard>
          )}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            accessibility={{ announcements: sortableAnnouncements(sessionKeys, sessionLabel), screenReaderInstructions: SORTABLE_INSTRUCTIONS }}
          >
            <SortableContext items={sessionKeys} strategy={verticalListSortingStrategy}>
              {sessions.map((session, index) => (
                <SortableSessionRow
                  key={session.key}
                  session={session}
                  index={index}
                  nameError={fieldErrors[`template-session-${session.key}`]}
                  onChange={changeSession}
                  onRemove={removeSession}
                />
              ))}
            </SortableContext>
          </DndContext>
          <div>
            <Button variant="secondary" icon={Plus} data-template-session-add onClick={addSession}>Add Session</Button>
          </div>
        </>
      )}

      {step === 3 && (
        <WizardCard>
          <TemplateSectionsEditor sections={sections} fieldErrors={fieldErrors} onChange={setSections} />
        </WizardCard>
      )}

      {step === 4 && (
        <>
          <WizardCard title="Default Pricing">
            <div className="classes-form-row">
              <PriceInput
                data-field="template-default-price"
                label="Default price per spot"
                currencyCode={currencyCode}
                error={defaultPriceError}
                value={form.default_price}
                onChange={(value) => setField("default_price", value)}
              />
              <Select label="Currency" disabled options={[{ value: currencyCode, label: currencyCode }]} value={currencyCode} />
            </div>
          </WizardCard>

          <WizardCard title="Default Policies">
            <Toggle
              label="Enable waitlist by default"
              description={`When new instances of this template fill up, registrants can join a waitlist, and a freed spot is held for ${waitlistWindowLabels[form.default_waitlist_claim_window_hours]}.`}
              checked={form.default_waitlist_enabled}
              onChange={(e) => setField("default_waitlist_enabled", toggleChecked(e))}
            />
            {form.default_waitlist_enabled && (
              <div className="classes-wizard__divided">
                <div className="classes-wizard__half">
                  <Select
                    label="Claim window"
                    hint="How long a promoted registrant has to claim the opened spot."
                    options={waitlistWindowOptions.map((h) => ({ value: h, label: `${h} hours` }))}
                    value={form.default_waitlist_claim_window_hours}
                    onChange={(v) => setField("default_waitlist_claim_window_hours", Number(v))}
                  />
                </div>
              </div>
            )}
          </WizardCard>
        </>
      )}

      {step === 5 && (
        <>
          <ReviewSection title="Basics" onEdit={() => goTo("basics")}>
            <ReviewRow label="Name" value={form.name || "—"} />
            <ReviewRow label="Category" value={form.category || "Uncategorized"} />
            <ReviewRow label="Internal code" value={form.internal_code || "—"} />
            <ReviewRow label="Description" value={form.description || "—"} />
            <ReviewRow label="Image" value={image.imageUrl ? <img src={image.imageUrl} alt="Template image" className="classes-image-preview" /> : "No image"} />
          </ReviewSection>

          <ReviewSection title="Format & Defaults" onEdit={() => goTo("defaults")}>
            <ReviewRow label="Format" value={FORMAT_LABELS[form.format] || form.format} />
            <ReviewRow label="Default # of sessions" value={sessions.length} />
            <ReviewRow label="Default session duration" value={durationInMinutes ? minutesToDurationText(durationInMinutes) : "—"} />
            <ReviewRow label="Default capacity" value={form.default_capacity || "—"} />
          </ReviewSection>

          <ReviewSection title="Curriculum" onEdit={() => goTo("curriculum")}>
            {sessions.length === 0 ? (
              <ReviewRow label="Sessions" value="No sessions" />
            ) : (
              <ol className="classes-wizard__review-list">
                {sessions.map((s) => (
                  <li key={s.key}>
                    <strong>{s.name || "Untitled"}</strong>
                    {s.description && <div className="classes-wizard__review-list-detail">{s.description}</div>}
                  </li>
                ))}
              </ol>
            )}
          </ReviewSection>

          <ReviewSection title="Page Content" onEdit={() => goTo("content")}>
            {keptSections(sections).length === 0 ? (
              <ReviewRow label="Sections" value="No page sections" />
            ) : (
              <ol className="classes-wizard__review-list">
                {keptSections(sections).map((section) => (
                  <li key={section.key}>
                    <strong>{sectionHeading(section) || "Untitled"}</strong>
                    {!sectionHasBody(section) && (
                      <div className="classes-wizard__review-list-detail">No text yet, so it stays hidden on the class page.</div>
                    )}
                  </li>
                ))}
              </ol>
            )}
          </ReviewSection>

          <ReviewSection title="Pricing & Policies" onEdit={() => goTo("pricing")}>
            <ReviewRow label="Default price per spot" value={form.default_price === "" ? "—" : (defaultPriceError ? form.default_price : formatPrice(form.default_price, currencyCode))} />
            <ReviewRow label="Currency" value={currencyCode} />
            <ReviewRow
              label="Waitlist by default"
              value={form.default_waitlist_enabled ? `Enabled · held ${waitlistWindowLabels[form.default_waitlist_claim_window_hours]}` : "Disabled"}
            />
          </ReviewSection>

          <TintedAlert
            color="brand"
            icon={InfoCircle}
            title="Templates don't go live until you schedule them"
            description="Saving creates a reusable template. To put a class on the calendar, schedule a new instance from this template after saving."
          />
        </>
      )}
    </WizardShell>
  );
}

ClassTemplateWizardApp.propTypes = {
  classTemplate: PropTypes.object,
  createPath: PropTypes.string,
  updatePath: PropTypes.string,
  indexPath: PropTypes.string.isRequired,
  csrfToken: PropTypes.string.isRequired,
  formatOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
  categoryOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
  waitlistWindowOptions: PropTypes.arrayOf(PropTypes.number).isRequired,
  waitlistWindowLabels: PropTypes.objectOf(PropTypes.string).isRequired,
  teamWaitlistDefault: PropTypes.bool,
  currencyCode: PropTypes.string,
};

ClassTemplateWizardApp.defaultProps = {
  classTemplate: null,
  teamWaitlistDefault: true,
  currencyCode: "USD",
};

export default ClassTemplateWizardApp;
